import { useSwitchTeam } from "hooks/useSwitchTeam";
import { useRef, useState, useEffect } from "react";
import ReactGA from "react-ga4";
import { useIntl } from "react-intl";

import {
  IUser,
  setAdminAccountDeleted,
  logoutDispatch,
  TSendMessageBody,
  setIsGenRes,
  setControllerState,
  clearControllerState,
  GetNewChatThread,
  getThreadTopicInfo,
  // setCreditLimitModal
} from "redux/actions";
import { useSelector } from "redux/hooks";
import { useAppNotification } from "../AppNotification";

// Import the EventType enum and handlers
import { EventType, isValidEventType } from "../ChatServices/EventTypes";
import * as EventHandlers from "../ChatServices/EventHandler";
import { removeQuotes } from "utils/functions";

export const useThreadStream = () => {
  const userDetail = useSelector((state) => state.authReducer.userDetail);
  const addHistory = useSelector((state) => state.chatReducer.addHistory);
  const { controller } = useSelector((state) => state.workSpaceReducer);
  const { formatMessage } = useIntl();

  // Ref to hold the latest value of addHistory
  const addHistoryRef = useRef(addHistory);

  const switchTeam = useSwitchTeam();
  const { triggerNotification } = useAppNotification();

  useEffect(() => {
    // Update the ref whenever addHistory changes
    addHistoryRef.current = addHistory;
  }, [addHistory]);

  let controllerRef = useRef<AbortController | null>(null);
  const [done, setDone] = useState<boolean | null>(null);
  let chatId = useRef<number>(0);
  let threadId = useRef<number>(0);
  let hasError = false;
  let notSupported = false;
    
  // Sync the local ref with Redux state
  useEffect(() => {
    controllerRef.current = controller;
  }, [controller]);

  // Function to process event-based stream data
  const processEventStream = (
    eventData: string, 
    data: TSendMessageBody, 
    admin_team_deleted: boolean
  ) => {
    // Split the received data into event chunks
    const events = eventData.split('event:').filter(event => event.trim());
    
    for (const event of events) {
      const parts = event.trim().split('data:');
      if (parts.length < 2) continue;
      
      const eventTypeStr = parts[0].trim();
      const eventContent = parts[1].trim();
      
      // Check if it's a valid event type
      if (!isValidEventType(eventTypeStr)) {
        continue;
      }
      
      // Cast to our enum type now that we've validated it
      const eventType = eventTypeStr as EventType;
      
      // Use a switch statement with the enum for type-safe handling
      switch (eventType) {
        case EventType.FILES_UPLOADED:
          EventHandlers.handleFilesUploaded(eventContent);
          break;
          
        case EventType.THREAD_UPDATED:
        case EventType.CHAT_UPDATED:
          const updated = JSON.parse(eventContent);
          chatId.current = updated.chat_id;
          threadId.current = updated?.id;
          EventHandlers.handleThreadUpdated(eventContent, data);
          break;
          
        case EventType.ASSISTANT_MESSAGE_CREATED:
          const updatedMes = JSON.parse(eventContent);
          chatId.current = updatedMes.chat_id;
          EventHandlers.handleAssistantMessageCreated(eventContent, data);
          break;
          
        case EventType.CREDITS_UPDATED:
          EventHandlers.handleCreditsUpdated(eventContent, userDetail ?? null);
          break;
          
        case EventType.IN_PROGRESS:
          EventHandlers.handleInProgress(eventContent, data);
          break;
          
        case EventType.USER_MESSAGE_CREATED:
          EventHandlers.handleUserMessageCreated(eventContent, data);
          break;
            
        case EventType.MESSAGE:
          EventHandlers.handleMessage(eventContent, data, admin_team_deleted);
          break;

        case EventType.CITATIONS:
  try {
    const citationsArray = JSON.parse(eventContent);
    EventHandlers.handleCitition(citationsArray, data);
  } catch (e) {
    console.error('Failed to parse citations:', eventContent, e);
  }
          break;
          
        case EventType.COMPLETED:
          // Update completed handler to set done state and manage thread-specific logic
          EventHandlers.handleCompleted(eventContent, data, admin_team_deleted);
          setDone(true);
          controllerRef.current = null;
          clearControllerState();
          
          // Handle thread-specific logic after completion
          setTimeout(() => {
            if (data.newChatBoolean && threadId.current) {
              getThreadTopicInfo(true);
              GetNewChatThread(threadId.current, data.workspace_id ?? 1, 1, 10)
                .then(() => {})
                .catch((err: any) => console.error(err));
              setTimeout(() => threadId.current = 0, 110);
            }
          }, 2000);
          break;

        case EventType.INCOMPLETE:
          EventHandlers.handleInComplete(eventContent, data);
          break;
          
        case EventType.CHAT_STARTED:
          break;

        case EventType.THINKING:
          EventHandlers.handleThinking(eventContent, data, admin_team_deleted);
          break;
          
          
        case EventType.ERROR:
          hasError = true;
          if(removeQuotes(eventContent) === "model not supported") {
            notSupported=true;
            break;
          }
          EventHandlers.handleErrorMessage(eventContent, data);
          break;
          
        default:
          console.log('Unhandled event type:', eventType, eventContent);
      }
    }
  };

  const ThreadStartStream = (data: TSendMessageBody) => {
    return new Promise(async (resolve, reject) => {
      if (data.chatType === "document") {
        ReactGA.event({
          action: "Chat_DocumentMessage_Submitted",
          category: "WebsiteMessageSentDocumentChat",
        });
      }
      try {
        controllerRef.current = new AbortController();
        const newController = new AbortController();
        setControllerState(newController);

        let body: any;
        // if (data.file_path && data.file_path.length > 0) {
        //   body = {
        //     message: data.message,
        //     newChatBoolean: data.newChatBoolean,
        //     thread_id: data.thread_id,
        //     model: data.model,
        //     file_path: data.file_path,
        //     regenerate: data.regenerate,
        //     workspace_id: data.workspace_id,
        //   };
        // } else if (data.images) {
        //   body = {
        //     message: data.message,
        //     newChatBoolean: data.newChatBoolean,
        //     thread_id: data.thread_id,
        //     model: data.model,
        //     images: data.images,
        //     regenerate: data.regenerate,
        //     workspace_id: data.workspace_id,
        //   };
        // } else {
          body = {
            message: data.message,
            newChatBoolean: data.newChatBoolean,
            ...(data.thread_id && { thread_id: data.thread_id}),
              ...(data.images && { images: data.images}),
             ...(data.file_path && data.file_path.length > 0 && {  file_path: data.file_path}),
            model: data.model,
            regenerate: data.regenerate,
            workspace_id: data.workspace_id,
          };
        // }

        const url = new URL(`${process.env.REACT_APP_API_BASEURL}/api/workspace-threads/chat`);

        // Append team_id as a query parameter
        url.searchParams.append("team_id", userDetail?.user?.team?.id.toString() ?? "");
        
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "text/event-stream", // Use event-stream for SSE
            "Content-Type": "application/json",
            Authorization: `Bearer ${userDetail?.token}`,
          },
          body: JSON.stringify(body),
          signal: controllerRef?.current.signal,
        });

        if (response?.status === 403) {
          logoutDispatch();
          triggerNotification({
            message: formatMessage({ id: "account.deleted.message" }),
            type: "error", 
          });
          return;
        }

        if (response.status === 422) {
          const json = await response.json();
          triggerNotification({
            message: json.message || "An error occurred. Please try again.",
            type: "error",
          });
          reject(json.message);
          return;
        }

        if (response.status === 400) {
          const json = await response.json();
          triggerNotification({
            message: json.message || "An error occurred. Please try again.",
            type: "error",
          });
          reject(json.message);
          return;
        }
        
        setIsGenRes(true);

        const reader = response?.body?.getReader();
        const decoder = new TextDecoder();
      
        setDone(false);
        let streamBuffer = '';
        let admin_team_deleted = false;
        let InsufficientCredits = false;
        
        // Add this alongside your completedEvent state
let citationsEvent = {
  collecting: false,
  content: '',
  startMarker: 'event: citations\ndata: ',
  endDetected: false
};

      let completedEvent = {
        collecting: false,
        content: '',
        startMarker: 'event: completed\ndata: ',
        endDetected: false
      };

        
        while (true) {
          const streamResult = await reader?.read();
          
          if (!streamResult?.value) {
            if (streamResult?.done) {

     // Process any final data in the buffer
     if (streamBuffer.trim()) {
      processEventStream(streamBuffer, data, admin_team_deleted);
    }
    
    // Process any collected but incomplete events
    if (citationsEvent.collecting && citationsEvent.content.trim()) {
      try {
        const citationsArray = JSON.parse(citationsEvent.content.trim());
        EventHandlers.handleCitition(citationsArray, data);
      } catch (e) {
        console.error('Failed to parse citations at stream end:', citationsEvent.content, e);
      }
    }

            if (completedEvent.collecting && completedEvent.content) {
              // Handle the completed event with full content
              EventHandlers.handleCompleted(completedEvent.content, data, admin_team_deleted);
              setDone(true);
              controllerRef.current = null;
              clearControllerState();
            }

              setDone(true);
              controllerRef.current = null;
              clearControllerState();
              
              // Handle thread topic update after completion
              setTimeout(() => {
                if (data.newChatBoolean && threadId.current) {
                  getThreadTopicInfo(true);
                  GetNewChatThread(threadId.current, data.workspace_id ?? 1, 1, 10)
                    .then(() => {})
                    .catch((err: any) => console.error(err));
                  setTimeout(() => threadId.current = 0, 110);
                }
              }, 2000);
              
              if (admin_team_deleted === true) {
                setAdminAccountDeleted(true);
                switchTeam();
                reject('Team has been deleted by admin');
              }
              if(InsufficientCredits) {
                reject('Insufficient credits');
              }

              if(notSupported) {
                reject('model not supported');
              }
              
              resolve({ ...streamResult, chatId: hasError? null : chatId.current });
              break;
            }
            continue;
          }
          
          const decodedChunk = decoder.decode(streamResult.value, { stream: !streamResult.done });
          streamBuffer += decodedChunk;

  // First look for citations events
  const citationsMarkerIndex = streamBuffer.indexOf('event: citations\ndata:');
  if (citationsMarkerIndex !== -1) {
    // Process everything up to the citations marker
    const beforeCitations = streamBuffer.substring(0, citationsMarkerIndex);
    if (beforeCitations.trim()) {
      processEventStream(beforeCitations, data, admin_team_deleted);
    }
    
    // Extract citations content
    const afterCitationsStart = streamBuffer.substring(citationsMarkerIndex + 'event: citations\ndata:'.length);
    const nextEventIndex = afterCitationsStart.indexOf('\nevent:');
    
    if (nextEventIndex !== -1) {
      // We have a complete citations event
      const citationsContent = afterCitationsStart.substring(0, nextEventIndex).trim();
      const remainingBuffer = afterCitationsStart.substring(nextEventIndex + 1); // +1 to remove the leading \n
      
      try {
        const citationsArray = JSON.parse(citationsContent);
        EventHandlers.handleCitition(citationsArray, data);
      } catch (e) {
        console.error('Failed to parse citations:', citationsContent, e);
      }
      
      // Update the buffer with the remaining content
      streamBuffer = remainingBuffer;
    } else {
      // Incomplete citations event - keep collecting
      citationsEvent.collecting = true;
      citationsEvent.content = afterCitationsStart;
      streamBuffer = ''; // Clear the processed part
    }
  } 
  // If we're collecting citations and don't have a new event yet
  else if (citationsEvent.collecting) {
    const nextEventIndex = streamBuffer.indexOf('\nevent:');
    
    if (nextEventIndex !== -1) {
      // We now have a complete citations event
      citationsEvent.content += streamBuffer.substring(0, nextEventIndex).trim();
      const remainingBuffer = streamBuffer.substring(nextEventIndex + 1);
      
      try {
        const citationsArray = JSON.parse(citationsEvent.content);
        EventHandlers.handleCitition(citationsArray, data);
      } catch (e) {
        console.error('Failed to parse citations:', citationsEvent.content, e);
      }
      
      // Reset citations collection and update buffer
      citationsEvent.collecting = false;
      citationsEvent.content = '';
      streamBuffer = remainingBuffer;
    } else {
      // Still collecting citations
      citationsEvent.content += streamBuffer;
      streamBuffer = '';
    }
  }
             // Special handling for completed event
      else if (!completedEvent.collecting && streamBuffer.includes(completedEvent.startMarker)) {
          // Start collecting a completed event
          completedEvent.collecting = true;
          const startIndex = streamBuffer.indexOf(completedEvent.startMarker) + completedEvent.startMarker.length;
          completedEvent.content = streamBuffer.substring(startIndex);
          
          // Clear the buffer as we're handling this specially
          streamBuffer = '';
          
          // Process other events that might be before the completed event
          const preCompletedContent = streamBuffer.substring(0, streamBuffer.indexOf(completedEvent.startMarker));
          if (preCompletedContent.trim()) {
            processEventStream(preCompletedContent, data, admin_team_deleted);
          }
        } 
        else if (completedEvent.collecting) {
          // Continue collecting the completed event content
          completedEvent.content += decodedChunk;
          
          if (streamBuffer.includes('\n\nevent:') || streamResult.done) {
            // Found a new event after completed or reached end
            completedEvent.endDetected = true;
            
            // Split at the next event marker if present
            const nextEventIndex = completedEvent.content.indexOf('\n\nevent:');
            if (nextEventIndex !== -1) {
              const remainingContent = completedEvent.content.substring(nextEventIndex);
              completedEvent.content = completedEvent.content.substring(0, nextEventIndex);
              streamBuffer = remainingContent; // Put the remainder back in the buffer
            }
            
            // Handle the completed event with full content
            EventHandlers.handleCompleted(completedEvent.content, data, admin_team_deleted);
            
            // Reset completed event state
            completedEvent = {
              collecting: false,
              content: '',
              startMarker: 'event: completed\ndata: ',
              endDetected: false
            };
          }
        } else if (streamBuffer.includes('event:')) {
            processEventStream(streamBuffer, data, admin_team_deleted);
            streamBuffer = ''; // Clear the buffer after processing
          } 

        else {
          try {
            const jsonData = JSON.parse(streamBuffer);
            if (jsonData.message === "Team not found") {
              admin_team_deleted = true;
              streamBuffer = '';
            }
            else if (jsonData.errors.message === "Insufficient credits") {
              InsufficientCredits= true;
              // setCreditLimitModal(true);
              streamBuffer = '';
            }
          } catch (e) {
            // Not valid JSON or we need to wait for more data
          }
          
        }}
        
        // Analytics events
        ReactGA.event({
          action: "Chat_Response_Success",
          category: (userDetail?.user as IUser).activeSubscription.name,
          label: new Date().toISOString(),
          value: 5,
        });

        ReactGA.event({
          action: "Credits_Usage_Tracked",
          category: "WebsiteChatScreenCreditUsed_category",
          label: new Date().toISOString(),
        });
        
      } catch (error) {
        console.error("Stream error:", error);
        reject(error);
      }
    });
  };

  // Create a cancel function that can be used from components
  const cancelThreadStream = () => {
    if (controller) {
      controller.abort();
      clearControllerState();
      setDone(true);
    }
  };
  
  return { done, setDone, ThreadStartStream, controllerRef, cancelThreadStream };
};